// Eskom PARCC — auto-drafted deck narratives (Wednesday Composer).
//
// Each draft is a pure function of a summary/gap row's NUMBERS — never the clock
// (the slide title carries the as-of date, so no date is baked into editable
// prose). The consultant edits them in the composer; a saved edit is kept until
// its numbers move, detected by comparing a numeric FINGERPRINT (draftInputs),
// not the rendered string. Pure; tested in test/features/reports/narratives.test.ts.
import { QUARTER_MONTHS, type Quarter } from '../../domain/fiscal';
import { gapsSelfDeclarationNarrative, type SummaryRow, type GapBreakdownRow } from './deckSections';

export type SlideKey =
  | 'gaps-sd'
  | 'divisional-intro'
  | 'divisional-footer'
  | 'kpi-cards'
  | 'quarter-intro'
  | 'quarter-footer'
  | 'quarter-range-label'
  | 'review-update';

const n = (v: number): string => (Number.isFinite(v) ? v : 0).toLocaleString('en-ZA');

/** The composite key for a persisted narrative row. */
export function narrativeKey(fy: string, scopeKey: string, slideKey: SlideKey, division = ''): string {
  return `${fy}|${scopeKey}|${slideKey}|${division}`;
}

/**
 * The numeric fingerprint a draft was built from — the staleness key. A wording
 * change to a template must NOT flag "numbers moved", so only these numbers are
 * compared, never the rendered string.
 */
export function draftInputs(row: Partial<SummaryRow & GapBreakdownRow>): Record<string, number> {
  const g = (v: number | undefined): number => (Number.isFinite(v) ? (v as number) : 0);
  return {
    population: g(row.population),
    reviewed: g(row.reviewed),
    compliant: g(row.compliant),
    selfDeclared: g(row.selfDeclared),
    outstanding: g(row.outstanding),
    notApplicable: g(row.notApplicable),
    linksOutstanding: g(row.linksOutstanding),
    ieSelfDeclared: g(row.ieSelfDeclared),
    ieOutstanding: g(row.ieOutstanding),
    pctReviewed: g(row.pctReviewed),
  };
}

/** True iff any fingerprint number moved since the draft was saved. */
export function narrativeIsStale(
  savedInputs: Record<string, number> | null | undefined,
  currentInputs: Record<string, number>,
): boolean {
  if (!savedInputs) return false;
  return Object.keys(currentInputs).some((k) => (savedInputs[k] ?? 0) !== (currentInputs[k] ?? 0));
}

/** A human diff of what moved, for the stale banner ("reviewed 50 → 202"). */
export function fingerprintDiff(
  savedInputs: Record<string, number>,
  currentInputs: Record<string, number>,
): string {
  const parts: string[] = [];
  for (const k of Object.keys(currentInputs)) {
    const from = savedInputs[k] ?? 0;
    const to = currentInputs[k] ?? 0;
    if (from !== to) parts.push(`${k} ${n(from)} → ${n(to)}`);
  }
  return parts.join(', ');
}

// ---- The drafts ------------------------------------------------------------

/** Slide 2 narrative box (self-declaration risk framing + the moving-target line). */
export function draftGapsSd(gapsRows: readonly GapBreakdownRow[]): string {
  const first = gapsSelfDeclarationNarrative(gapsRows);
  const movers = gapsRows.filter(
    (r) => r.key !== 'Total' && r.outstanding > 0 && (r.key === 'NTCSA' || r.key === 'ICT & Professional Services'),
  );
  if (movers.length === 0) return first;
  const names = movers.map((r) => (r.key === 'ICT & Professional Services' ? 'ICT' : r.key)).join(' and ');
  const second =
    `The ${names} outstanding gaps remain a moving target, as some of the transactions are currently in the ` +
    'process of being self-declared, while others are being cleared by buyers. The Rand value of the potential IE ' +
    'will be confirmed once all resolved and self-declared transactions have been accounted for.';
  return `${first} ${second}`;
}

/** Slide 3 intro line. */
export function draftDivisionalIntro(total: SummaryRow): string {
  return `A total population of ${n(total.population)} transactions was received with ${n(total.reviewed)} reviews completed.`;
}

/** Slide 3 footer box. */
export function draftDivisionalFooter(total: SummaryRow): string {
  const na = total.notApplicable > 0 ? ` ${n(total.notApplicable)} were assessed as not applicable.` : '';
  const links =
    total.linksOutstanding > 0 ? ` ${n(total.linksOutstanding)} transaction(s) still have links outstanding.` : '';
  return (
    `From a population of ${n(total.population)}, a total of ${n(total.reviewed)} transactions were reviewed. ` +
    `Of these, ${n(total.compliant)} were fully compliant, while ${n(total.selfDeclared)} are to be closed via ` +
    `self-declaration.${na} ${n(total.outstanding)} gaps remain outstanding.${links}`
  );
}

/** Slide 6 KPI narrative box (her four sentences). */
export function draftKpiCards(total: SummaryRow): string {
  const pct = Number.isFinite(total.pctReviewed) ? total.pctReviewed : 0;
  return (
    `${n(total.reviewed)} reviews completed from a population of ${n(total.population)}. ${pct}% review completion. ` +
    `${n(total.compliant)} transactions fully resolved / compliant. ${n(total.selfDeclared)} transactions closed via self-declaration`
  );
}

/** Per-quarter slide intro line. */
export function draftQuarterIntro(total: SummaryRow): string {
  return `A total population of ${n(total.population)} transactions received from the divisions.`;
}

/** Per-quarter slide footer line. */
export function draftQuarterFooter(total: SummaryRow): string {
  const pct = Number.isFinite(total.pctReviewed) ? total.pctReviewed : 0;
  return (
    `${n(total.reviewed)} reviews completed – ${pct}%. ${n(total.compliant)} transactions are fully compliant with ` +
    `${n(total.outstanding)} gaps outstanding.`
  );
}

/** Per-quarter month-range label (the parenthesised title slot). */
export function draftQuarterRangeLabel(quarter: Quarter): string {
  return QUARTER_MONTHS[quarter].label;
}

/** Optional per-division review-update footer line. */
export function draftReviewUpdate(total: SummaryRow): string {
  return `${n(total.reviewed)} of ${n(total.population)} reviewed; ${n(total.compliant)} compliant, ${n(total.selfDeclared)} self-declared, ${n(total.outstanding)} outstanding.`;
}
